import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Users, Download, TrendingUp, DollarSign, ShieldCheck } from 'lucide-react';

export default function AdminTab({ auth }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingProfiles, setExportingProfiles] = useState(false);
  const [exportingPerformances, setExportingPerformances] = useState(false);

  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const [mergingAgata, setMergingAgata] = useState(false);
  const [recomputingMedals, setRecomputingMedals] = useState(false);
  const [recomputeMonth, setRecomputeMonth] = useState('2026-01');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load admin stats', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type: type });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportProfiles = async () => {
    setExportingProfiles(true);
    try {
      const response = await axios.get(`${API}/admin/export/profiles`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      downloadFile(response.data.csv, 'member_profiles.csv', 'text/csv;charset=utf-8;');
      toast.success('Profiles exported successfully!');
    } catch (error) {
      console.error('Failed to export profiles', error);
      toast.error('Failed to export profiles');
    } finally {
      setExportingProfiles(false);
    }
  };

  const handleExportPerformances = async () => {
    setExportingPerformances(true);
    try {
      const response = await axios.get(`${API}/admin/export/performances`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      downloadFile(response.data.csv, 'member_performances_2026.csv', 'text/csv;charset=utf-8;');
      toast.success('Performances exported successfully!');
    } catch (error) {
      console.error('Failed to export performances', error);
      toast.error('Failed to export performances');
    } finally {
      setExportingPerformances(false);
    }
  };

  const [cleaningImages, setCleaningImages] = useState(false);

  const handleCleanImages = async () => {
    if (!window.confirm('Are you sure you want to scan and clean metadata from ALL images? This might take a while.')) {
      return;
    }

    setCleaningImages(true);
    try {
      const response = await axios.post(`${API}/admin/clean-images`, {}, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const stats = response.data.stats;
      toast.success(`Cleaned ${stats.users_updated} users and ${stats.entries_updated} entries!`);
    } catch (error) {
      console.error('Failed to clean images', error);
      toast.error('Failed to clean image metadata');
    } finally {
      setCleaningImages(false);
    }
  };

  const [exportingDatabase, setExportingDatabase] = useState(false);

  const handleExportDatabase = async () => {
    setExportingDatabase(true);
    try {
      const response = await axios.get(`${API}/admin/export-db`, {
        headers: { Authorization: `Bearer ${auth.token}` },
        responseType: 'blob' // Important for binary/large JSON downloads
      });
      // Content-Disposition header usually handles filename, but we can also set a default
      // For blob response, axios returns the blob data directly

      // We need to create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Try to get filename from header
      let filename = `full_backup_${new Date().toISOString().split('T')[0]}.json`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch.length === 2)
            filename = filenameMatch[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Database exported successfully!');
    } catch (error) {
      console.error('Failed to export database', error);
      toast.error('Failed to export database');
    } finally {
      setExportingDatabase(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !resetPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!window.confirm(`Are you sure you want to reset the password for ${resetEmail}?`)) {
      return;
    }

    setResettingPassword(true);
    try {
      await axios.post(`${API}/admin/reset-password`, {
        email: resetEmail,
        password: resetPassword
      }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      toast.success('Password reset successfully');
      setResetEmail('');
      setResetPassword('');
    } catch (error) {
      console.error('Failed to reset password', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
        setResettingPassword(false);
    }
  };

  const handleMergeAgata = async () => {
    if (!window.confirm('Are you sure you want to merge Agata\'s accounts?')) {
        return;
    }
    setMergingAgata(true);
    try {
        const response = await axios.post(`${API}/admin/merge-agata-accounts`, {}, {
            headers: { Authorization: `Bearer ${auth.token}` }
        });
        toast.success(response.data.message);
    } catch (error) {
        console.error('Failed to merge accounts', error);
        toast.error(error.response?.data?.detail || 'Failed to merge accounts');
    } finally {
        setMergingAgata(false);
    }
  };

  const handleRecomputeMedals = async () => {
    if (!window.confirm(`Are you sure you want to recompute medals for all users for ${recomputeMonth}? This might take a few seconds.`)) {
      return;
    }

    setRecomputingMedals(true);
    const toastId = toast.loading(`Recomputing medals for ${recomputeMonth}...`);
    try {
      const response = await axios.post(`${API}/admin/recompute-medals`, { month: recomputeMonth }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      toast.success(response.data.message || 'Medals recomputed successfully!', { id: toastId });
    } catch (error) {
      console.error('Failed to recompute medals', error);
      toast.error(error.response?.data?.detail || 'Failed to recompute medals', { id: toastId });
    } finally {
      setRecomputingMedals(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-6" data-testid="admin-tab">
        <Card className="p-6 glass">
          <div className="text-center text-gray-600">Loading statistics...</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6" data-testid="admin-tab">
      {/* Header */}
      <Card className="p-6 glass" data-testid="admin-header">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Admin Dashboard</h2>
        <p className="text-gray-600">Manage and monitor the 12 Challenges community</p>
      </Card>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Total Members */}
          <Card className="p-6 glass" data-testid="total-members-card">
            <div className="flex items-center gap-4">
              <div className="bg-teal-100 p-4 rounded-full">
                <Users className="text-teal-600" size={32} />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-800">{stats.total_members}</p>
                <p className="text-gray-600">Total Members</p>
              </div>
            </div>
          </Card>

          {/* Payment Status */}
          <Card className="p-6 glass" data-testid="payment-status-card">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-4 rounded-full">
                <DollarSign className="text-green-600" size={32} />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-600">{stats.paid_members}</p>
                <p className="text-gray-600">Paid Members</p>
                <p className="text-sm text-gray-500 mt-1">{stats.unpaid_members} unpaid</p>
              </div>
            </div>
          </Card>

          {/* Gender Breakdown */}
          <Card className="p-6 glass col-span-1 md:col-span-2" data-testid="gender-breakdown-card">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="text-teal-600" size={24} />
              <h3 className="text-xl font-semibold text-gray-800">Gender Breakdown</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{stats.gender_breakdown.male}</p>
                <p className="text-gray-600">Male</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-2xl font-bold text-pink-600">{stats.gender_breakdown.female}</p>
                <p className="text-gray-600">Female</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{stats.gender_breakdown.other}</p>
                <p className="text-gray-600">Other</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Export Options */}
      <Card className="p-6 glass" data-testid="export-section">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Export Data</h3>
        <div className="space-y-4">
          {/* Export Profiles */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="pr-4">
              <p className="font-semibold text-gray-800">Member Profiles</p>
              <p className="text-sm text-gray-600">Export all member profile information (ID, email, name, location, gender, payment status)</p>
            </div>
            <Button
              onClick={handleExportProfiles}
              disabled={exportingProfiles}
              className="bg-teal-500 hover:bg-teal-600 flex items-center gap-2"
              data-testid="export-profiles-button"
            >
              <Download size={16} />
              {exportingProfiles ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>

          {/* Export Performances */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="pr-4">
              <p className="font-semibold text-gray-800">Member Performances</p>
              <p className="text-sm text-gray-600">Export daily challenge performance for all members throughout 2026 (Name, Surname + 365 day columns)</p>
            </div>
            <Button
              onClick={handleExportPerformances}
              disabled={exportingPerformances}
              className="bg-teal-500 hover:bg-teal-600 flex items-center gap-2"
              data-testid="export-performances-button"
            >
              <Download size={16} />
              {exportingPerformances ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>

          {/* Export Full Database */}
          <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="pr-4">
              <p className="font-semibold text-gray-800">Full Database Backup</p>
              <p className="text-sm text-gray-600">Download a complete JSON dump of the database (Users, Entries, Likes, Comments). Useful for backups/restoration.</p>
            </div>
            <Button
              onClick={handleExportDatabase}
              disabled={exportingDatabase}
              className="bg-amber-500 hover:bg-amber-600 flex items-center gap-2 text-white"
              data-testid="export-database-button"
            >
              <Download size={16} />
              {exportingDatabase ? 'Exporting...' : 'Export JSON'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Maintenance */}
      <Card className="p-6 glass" data-testid="maintenance-section">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Maintenance</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="pr-4">
              <p className="font-semibold text-gray-800">Clean Image Metadata</p>
              <p className="text-sm text-gray-600">Scan all existing images (profiles and entries), strip EXIF metadata, resize, and compress according to the new policy.</p>
            </div>
            <Button
              onClick={handleCleanImages}
              disabled={cleaningImages}
              className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2 text-white"
            >
              <ShieldCheck size={16} />
              {cleaningImages ? 'Cleaning...' : 'Clean All Images'}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="pr-4">
              <p className="font-semibold text-gray-800">Refresh Leaderboard</p>
              <p className="text-sm text-gray-600">Recalculate leaderboard stats for all users based on their entries. Use this if the leaderboard seems out of sync or empty.</p>
            </div>
            <Button
              onClick={async () => {
                if (!window.confirm('Recalculate leaderboard for ALL users? This might take a few seconds.')) return;
                const toastId = toast.loading('Refreshing leaderboard...');
                try {
                   const response = await axios.post(`${API}/admin/refresh-leaderboard`, {}, {
                     headers: { Authorization: `Bearer ${auth.token}` }
                   });
                   toast.success(response.data.message || 'Leaderboard refreshed!', { id: toastId });
                } catch (error) {
                   console.error('Failed to refresh leaderboard', error);
                   toast.error('Failed to refresh leaderboard', { id: toastId });
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2 text-white"
            >
              <TrendingUp size={16} />
              Refresh Stats
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200 gap-4">
            <div className="pr-4">
              <p className="font-semibold text-gray-800">Recompute Medals</p>
              <p className="text-sm text-gray-600">Recalculate medals for all users for a specific month. Clears stale medals if completion drops below 75%.</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={recomputeMonth}
                onChange={(e) => setRecomputeMonth(e.target.value)}
                className="p-2 rounded border border-gray-300 text-sm bg-white"
              >
                <option value="2026-01">January 2026</option>
                <option value="2026-02">February 2026</option>
                <option value="2026-03">March 2026</option>
                <option value="2026-04">April 2026</option>
                <option value="2026-05">May 2026</option>
                <option value="2026-06">June 2026</option>
                <option value="2026-07">July 2026</option>
                <option value="2026-08">August 2026</option>
                <option value="2026-09">September 2026</option>
                <option value="2026-10">October 2026</option>
                <option value="2026-11">November 2026</option>
                <option value="2026-12">December 2026</option>
              </select>
              <Button
                onClick={handleRecomputeMedals}
                disabled={recomputingMedals}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2 text-white whitespace-nowrap"
              >
                <ShieldCheck size={16} />
                {recomputingMedals ? 'Recomputing...' : 'Recompute'}
              </Button>
            </div>
          </div>
        </div>
      </Card>




      {/* User Management */}
      <Card className="p-6 glass" data-testid="user-management-section">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">User Management</h3>
        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
             <h4 className="font-semibold text-red-800 mb-2">Force Password Reset</h4>
             <p className="text-sm text-red-600 mb-4">
               Override a user's password. This action cannot be undone.
             </p>
             <form onSubmit={handleResetPassword} className="flex flex-col gap-3">
               <div>
                 <input
                   type="email"
                   placeholder="User Email"
                   className="w-full p-2 rounded border border-gray-300"
                   value={resetEmail}
                   onChange={(e) => setResetEmail(e.target.value)}
                 />
               </div>
               <div>
                  <input
                    type="text"
                    placeholder="New Password"
                    className="w-full p-2 rounded border border-gray-300"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                  />
               </div>
               <Button
                 type="submit"
                 disabled={resettingPassword}
                 className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
               >
                 {resettingPassword ? 'Resetting...' : 'Reset Password'}
               </Button>
             </form>
          </div>

          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
             <h4 className="font-semibold text-orange-800 mb-2">Merge Accounts (Agata)</h4>
             <p className="text-sm text-orange-600 mb-4">
               Merge Agata's old account into her new one and backfill Jan 4-6.
             </p>
             <Button
               onClick={handleMergeAgata}
               disabled={mergingAgata}
               className="bg-orange-600 hover:bg-orange-700 text-white"
             >
               {mergingAgata ? 'Merging...' : 'Merge Agata Accounts'}
             </Button>
          </div>
        </div>
      </Card>


      {/* Information Note */}
      <Card className="p-6 glass bg-blue-50 border-blue-200">
        <p className="text-sm text-gray-700">
          <strong>Note:</strong> All exported data is for administrative purposes only.
          Please handle member information with care and in accordance with data protection regulations.
        </p>
      </Card>
    </div>
  );
}
